import React, { useEffect, useRef, useState } from "react"
import { Alert, Badge, Button, Card, Spinner, Table } from "react-bootstrap"
import { useSelector } from "react-redux"
import { Navigate } from "react-router-dom"

import recordNotFound from "../../../assets/images/record_not_found.png"
import CustomPagination from "../../../components/CustomPagination"
import ModalComponent from "../../../components/Modal"
import PageTitleButton from "../../../components/PageTitleButton"
import SearchBox from "../../../components/SearchBox"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import { useHandleUnauthorizedAccess } from "../../../lib/GlobalMethods"
import { modulePermission } from "../../../lib/GlobalVariables"
import useDebounce from "../../../lib/hooks/useDebounce"
import MinimumBalanceModal from "./MinimumBalanceModal"
import {
  formatMinorUnits,
  mapWalletSummaryResponse,
} from "./walletSummary"

const PAGE_SIZE = 20

const apiErrorMessage = (response, fallback) =>
  response?.response?.data?.message ||
  response?.data?.message ||
  response?.message ||
  fallback

const formatDate = (value) =>
  value ? new Date(value).toLocaleString() : "—"

const transactionBadge = {
  COMPLETED: "success",
  PENDING: "warning",
  FAILED: "danger",
  CANCELLED: "secondary",
}

const FleetVendorWallets = () => {
  const { userDetails } = useSelector((state) => state.basicInformation)
  const modulePermissionList = useSelector(
    (state) => state.modulePermissions.modulePermissionList,
  )
  const canViewWallets =
    userDetails?.role === "Super Admin" &&
    modulePermissionList?.[modulePermission.fleetManagement]?.some(
      (permission) => permission.permission === "view",
    )
  const canEditMinimumBalance =
    userDetails?.role === "Super Admin" &&
    modulePermissionList?.[modulePermission.fleetManagement]?.some(
      (permission) => permission.permission === "edit",
    )

  const [wallets, setWallets] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [refreshKey, setRefreshKey] = useState(0)
  const [summary, setSummary] = useState(null)
  const [minimumBalance, setMinimumBalance] = useState(null)
  const [showMinimumBalanceModal, setShowMinimumBalanceModal] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [selectedWallet, setSelectedWallet] = useState(null)
  const [walletDetails, setWalletDetails] = useState(null)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [detailsError, setDetailsError] = useState("")
  const detailsRequestId = useRef(0)
  const walletListRequest = useRef({ key: "", promise: null })
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  const { handleAccess } = useHandleUnauthorizedAccess()

  useEffect(() => {
    if (!canViewWallets) return undefined
    let active = true

    const fetchWallets = async () => {
      setLoading(true)
      setError("")
      const params = {
        page: String(pagination.current_page),
        limit: String(PAGE_SIZE),
      }
      if (debouncedSearchQuery.trim()) {
        params.search_term = debouncedSearchQuery.trim()
      }

      let requestPromise
      try {
        const requestKey = JSON.stringify(params)
        requestPromise = walletListRequest.current.promise

        if (walletListRequest.current.key !== requestKey || !requestPromise) {
          requestPromise = axiosProvider({
            method: "GET",
            endpoint: admin.fleetVendorWalletList,
            params,
          })
          walletListRequest.current = {
            key: requestKey,
            promise: requestPromise,
          }
        }

        const response = await requestPromise
        if (!active) return
        handleAccess({ code: response?.data?.code })
        if (response?.data?.code !== 200) {
          throw new Error(
            apiErrorMessage(response, "Unable to load Fleet Vendor wallets"),
          )
        }
        setWallets(response.data.data || [])
        const nextSummary = mapWalletSummaryResponse(response)
        if (nextSummary.currency !== "INR") {
          throw new Error("Wallet summary returned an unsupported currency")
        }
        setSummary(nextSummary)
        setMinimumBalance(response.data.minimum_balance || null)
        setPagination(
          response.data.pagination || { current_page: 1, total_pages: 1 },
        )
      } catch (requestError) {
        if (!active) return
        setWallets([])
        setSummary(null)
        setMinimumBalance(null)
        setError(
          requestError?.message || "Unable to load Fleet Vendor wallets",
        )
      } finally {
        if (walletListRequest.current.promise === requestPromise) {
          walletListRequest.current.promise = null
        }
        if (active) setLoading(false)
      }
    }

    fetchWallets()
    return () => {
      active = false
    }
    // handleAccess follows the existing authenticated-list convention.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canViewWallets,
    debouncedSearchQuery,
    pagination.current_page,
    refreshKey,
  ])

  useEffect(
    () => () => {
      detailsRequestId.current += 1
    },
    [],
  )

  const fetchWalletDetails = async (walletEntry, requestId) => {
    setDetailsLoading(true)
    setDetailsError("")
    try {
      const vendorId = encodeURIComponent(walletEntry.fleet_vendor.id)
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.fleetVendorWalletVendorTransactions}${vendorId}/transactions`,
        params: { page: "1", limit: String(PAGE_SIZE) },
      })
      if (requestId !== detailsRequestId.current) return
      handleAccess({ code: response?.data?.code })
      if (response?.data?.code !== 200) {
        throw new Error(apiErrorMessage(response, "Unable to load wallet details"))
      }
      setWalletDetails({
        fleet_vendor: response.data.fleet_vendor,
        wallet: response.data.wallet,
        transactions: response.data.data || [],
      })
    } catch (requestError) {
      if (requestId !== detailsRequestId.current) return
      setDetailsError(requestError?.message || "Unable to load wallet details")
    } finally {
      if (requestId === detailsRequestId.current) setDetailsLoading(false)
    }
  }

  const openDetails = (walletEntry) => {
    const requestId = detailsRequestId.current + 1
    detailsRequestId.current = requestId
    setSelectedWallet(walletEntry)
    setWalletDetails(null)
    setShowDetails(true)
    fetchWalletDetails(walletEntry, requestId)
  }

  const closeDetails = () => {
    detailsRequestId.current += 1
    setShowDetails(false)
    setSelectedWallet(null)
    setWalletDetails(null)
    setDetailsError("")
    setDetailsLoading(false)
  }

  const retryDetails = () => {
    if (!selectedWallet || detailsLoading) return
    const requestId = detailsRequestId.current + 1
    detailsRequestId.current = requestId
    fetchWalletDetails(selectedWallet, requestId)
  }

  if (!canViewWallets) return <Navigate to="/dashboard" replace />

  const selectedVendor =
    walletDetails?.fleet_vendor || selectedWallet?.fleet_vendor
  const selectedVendorWallet = walletDetails?.wallet || selectedWallet?.wallet
  const transactions = walletDetails?.transactions || []

  const summaryCards = [
    ["Total Fleet Vendors", summary?.totalVendors, "bg_sea_green"],
    [
      "Total Fleet Wallet Balance",
      summary && formatMinorUnits(summary.totalWalletBalanceMinor),
      "bg_light_orange",
    ],
    [
      "Minimum Wallet Balance",
      minimumBalance && `₹${minimumBalance.minimum_balance}`,
      "bg_light_green",
    ],
  ]

  return (
    <>
      <PageTitleButton
        PagetitleText="Fleet Vendor Wallets"
        HeaderButtonText={canEditMinimumBalance ? "Update Minimum Balance" : ""}
        onClick={() => setShowMinimumBalanceModal(true)}
      />

      <div className="details_cards_wrapper mb-3">
        {summaryCards.map(
          ([label, value, className]) => (
            <div key={label} className={`${className} details_card pe-none`}>
              <div className="card_titleicon">
                <h3 className="card_title">{label}</h3>
              </div>
              <p className="tls_numbers">
                {loading ? (
                  <Spinner animation="border" size="sm" role="status">
                    <span className="visually-hidden">Loading {label}</span>
                  </Spinner>
                ) : error ? (
                  "Unavailable"
                ) : (
                  value ?? 0
                )}
              </p>
            </div>
          ),
        )}
      </div>

      <Card className="mb-3">
        <Card.Body>
          <SearchBox
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value)
              setPagination((current) => ({
                ...current,
                current_page: 1,
              }))
            }}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-body-secondary">
              <Spinner animation="border" size="sm" />
              Loading Fleet Vendor wallets...
            </div>
          ) : error ? (
            <Alert variant="danger" className="mb-0">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <span>{error}</span>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => setRefreshKey((value) => value + 1)}
                >
                  Retry
                </Button>
              </div>
            </Alert>
          ) : (
            <>
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th>Vendor Name</th>
                    <th>Vendor ID</th>
                    <th>Contact</th>
                    <th>Rate (₹/kWh)</th>
                    <th>Wallet Balance</th>
                    <th>Wallet Status</th>
                    <th>Created / Updated</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {wallets.length ? (
                    wallets.map((walletEntry) => (
                      <tr key={walletEntry.fleet_vendor.id}>
                        <td>{walletEntry.fleet_vendor.name}</td>
                        <td>{walletEntry.fleet_vendor.id}</td>
                        <td>{walletEntry.fleet_vendor.contact_no || "—"}</td>
                        <td>{walletEntry.fleet_vendor.cost_per_kwh != null ? `₹${walletEntry.fleet_vendor.cost_per_kwh}/kWh` : "—"}</td>
                        <td>
                          {walletEntry.wallet
                            ? formatMinorUnits(walletEntry.wallet.balance_minor)
                            : "—"}
                        </td>
                        <td>
                          <Badge
                            bg={walletEntry.wallet ? "success" : "secondary"}
                          >
                            {walletEntry.wallet
                              ? "Initialized"
                              : "Not Initialized"}
                          </Badge>
                        </td>
                        <td>
                          <small className="d-block">
                            Created: {formatDate(walletEntry.wallet?.createdAt)}
                          </small>
                          <small className="d-block">
                            Updated: {formatDate(walletEntry.wallet?.updatedAt)}
                          </small>
                        </td>
                        <td className="text-center">
                          <TooltipComponent
                            toolplace="top"
                            tooltipText="View Details"
                          >
                            <Button
                              variant="btnedit_icon"
                              className="btn_icon"
                              aria-label={`View wallet details for ${walletEntry.fleet_vendor.name}`}
                              onClick={() => openDetails(walletEntry)}
                            >
                              <i className="icon_main eye_open"></i>
                            </Button>
                          </TooltipComponent>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="text-center py-4" colSpan={7}>
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {pagination.total_pages > 1 && (
                <div className="mt-3">
                  <CustomPagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    onPageChange={(page) =>
                      setPagination((current) => ({
                        ...current,
                        current_page: page,
                      }))
                    }
                  />
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <ModalComponent
        show={showDetails}
        onHide={closeDetails}
        modeltitle="Fleet Vendor Wallet Details"
        modalsize="xl"
        btnclosetext="Close"
      >
        {detailsLoading ? (
          <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-body-secondary">
            <Spinner animation="border" size="sm" />
            Loading wallet details...
          </div>
        ) : detailsError ? (
          <Alert variant="danger" className="mb-0">
            <div className="d-flex align-items-center justify-content-between gap-3">
              <span>{detailsError}</span>
              <Button size="sm" variant="outline-danger" onClick={retryDetails}>
                Retry
              </Button>
            </div>
          </Alert>
        ) : (
          <>
            <div className="row g-3 mb-4">
              <div className="col-sm-6 col-lg-3">
                <small className="text-body-secondary d-block">Vendor</small>
                <strong>{selectedVendor?.name || "—"}</strong>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-body-secondary d-block">Vendor ID</small>
                <strong>{selectedVendor?.id || "—"}</strong>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-body-secondary d-block">Contact</small>
                <strong>{selectedVendor?.contact_no || "—"}</strong>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-body-secondary d-block">Rate (₹/kWh)</small>
                <strong>{selectedVendor?.cost_per_kwh != null ? `₹${selectedVendor.cost_per_kwh}/kWh` : "—"}</strong>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-body-secondary d-block">
                  Vendor Status
                </small>
                <Badge
                  bg={selectedVendor?.status === "ACTIVE" ? "success" : "secondary"}
                >
                  {selectedVendor?.status || "—"}
                </Badge>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-body-secondary d-block">
                  Wallet Status
                </small>
                <Badge bg={selectedVendorWallet ? "success" : "secondary"}>
                  {selectedVendorWallet ? "Initialized" : "Not Initialized"}
                </Badge>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-body-secondary d-block">Balance</small>
                <strong>
                  {selectedVendorWallet
                    ? formatMinorUnits(selectedVendorWallet.balance_minor)
                    : "—"}
                </strong>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-body-secondary d-block">Created</small>
                <strong>{formatDate(selectedVendorWallet?.createdAt)}</strong>
              </div>
              <div className="col-sm-6 col-lg-3">
                <small className="text-body-secondary d-block">Updated</small>
                <strong>{formatDate(selectedVendorWallet?.updatedAt)}</strong>
              </div>
            </div>

            <h6>Recent Wallet Transactions</h6>
            {transactions.length ? (
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Payment Method</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Payment Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td className="text-break">
                        {transaction.reference || "—"}
                      </td>
                      <td>{transaction.transaction_type || "—"}</td>
                      <td>{formatMinorUnits(transaction.amount_minor)}</td>
                      <td>{transaction.payment_method || "—"}</td>
                      <td>
                        <Badge
                          bg={
                            transactionBadge[transaction.status] || "secondary"
                          }
                        >
                          {transaction.status}
                        </Badge>
                      </td>
                      <td>{formatDate(transaction.createdAt)}</td>
                      <td className="text-break">
                        {transaction.razorpay_payment_id || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="mb-0 py-4 text-center text-body-secondary">
                No wallet transactions found.
              </p>
            )}
          </>
        )}
      </ModalComponent>

      <MinimumBalanceModal
        show={showMinimumBalanceModal}
        minimumBalance={minimumBalance?.minimum_balance}
        onHide={() => setShowMinimumBalanceModal(false)}
        onUpdated={(updatedSetting) => {
          setMinimumBalance(updatedSetting)
          setShowMinimumBalanceModal(false)
        }}
      />
    </>
  )
}

export default FleetVendorWallets
